import { Immutable } from "immer";
import React from "react";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";

import { templates } from "@reactivated";
import type {
    PillowsPromoConditionalBlock,
    PopularProductsBlock as PopularProductsValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { BrandMessageBlock } from "../../blocks/BrandMessageBlock";
import { CustomerReviewsBlock } from "../../blocks/CustomerReviewsBlock";
import { FeaturedProdBlock } from "../../blocks/FeaturedProdBlock";
import { PopularProductsBlock } from "../../blocks/PopularProductsBlock";
import { SleepSelectionBlock } from "../../blocks/SleepSelectionBlock";
import { ValuePropBlock } from "../../blocks/ValuePropBlock";
import {
    pillowsPromoConditionalInnerMap,
    toolboxComponentsMap,
} from "../../blocks/toolboxComponentsMap";

/**
 * Component map for the pillows HomePage body (``HomePageContent``). Exported
 * so the block-screenshot styleguide harness renders these blocks through the
 * exact same map production uses (see ``StyleguideBlockTemplate.tsx``).
 */
export const homePageBodyComponentsMap = {
    ...toolboxComponentsMap,
    promo_conditional_block: (block: {
        value: Immutable<PillowsPromoConditionalBlock>;
    }) => (
        <PromoConditionalBlock value={block.value}>
            <StreamField
                tagName={React.Fragment}
                value={block.value.content ?? []}
                components={pillowsPromoConditionalInnerMap}
            />
        </PromoConditionalBlock>
    ),
    popular_products_block: (block: {
        value: Immutable<PopularProductsValue>;
    }) => <PopularProductsBlock value={block.value} />,
    value_prop_block: ValuePropBlock,
    brand_message_block: BrandMessageBlock,
    featured_prop_block: FeaturedProdBlock,
    sleep_selection_block: SleepSelectionBlock,
    customer_reviews_block: CustomerReviewsBlock,
} as const;

/**
 * Pillows HomePage body assembly: dispatches every block type in
 * ``HomePageContent`` to its corresponding React component, collapsing the
 * N legacy ``{% include_block %}`` hydration roots into a single React
 * render tree.
 */
export const HomePageBody = (
    props: Immutable<{ page: templates.HomePageTemplate["page"] }>,
): React.JSX.Element | null => {
    if (!props.page.pillows_props) {
        return null;
    }
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.pillows_props.value}
            components={homePageBodyComponentsMap}
        />
    );
};
